import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// sessionStorage, not router state: Okta / Google / Microsoft logins leave the
// app and come back, which drops router state.
const KEY = 'zl_help_login_redirect';

// New users finish these first; the link opens on the next normal page.
const SKIP_ROUTES = ['/onboarding', '/confirmation', '/create-account'];

// Only same-origin paths, so a crafted link can't send users off-site.
const toSafePath = (path: string | null): string | null => {
  if (!path) return null;
  try {
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

// A shared Help link (?help=article:{id}) opened while logged out is kept
// through login, then reopened. Links without `help=` are never touched, so
// every other post-login landing stays as it was.
export const useHelpLoginRedirect = (token: string | null | undefined) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  // A Help link seen while logged in, with the page it came on.
  const carriedRef = useRef<{ help: string; from: string } | null>(null);

  useEffect(() => {
    try {
      if (!token) {
        if (new URLSearchParams(search).has('help')) {
          const path = toSafePath(`${pathname}${search}`);
          if (path) sessionStorage.setItem(KEY, path);
        }
        return;
      }

      // No access to the linked page: the route guard sends the user to
      // another page and the help param is lost. Carry it over, so Help still
      // opens (and shows "not available for your role" if the article is hidden).
      const params = new URLSearchParams(search);
      const help = params.get('help');
      if (help) {
        carriedRef.current = { help, from: pathname };
      } else if (carriedRef.current) {
        const carried = carriedRef.current;
        carriedRef.current = null;
        if (carried.from !== pathname && !SKIP_ROUTES.includes(pathname)) {
          params.set('help', carried.help);
          const _ = navigate(
            { pathname, search: `?${params}` },
            { replace: true }
          );
          return;
        }
      }

      const saved = toSafePath(sessionStorage.getItem(KEY));
      if (!saved || SKIP_ROUTES.includes(pathname)) return;
      sessionStorage.removeItem(KEY);
      if (saved !== `${pathname}${search}`) {
        const _ = navigate(saved, { replace: true });
      }
    } catch {
      // Storage unavailable (e.g. private mode): the link just won't survive login.
    }
    // Runs on login state and route changes only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pathname, search]);
};

export default useHelpLoginRedirect;
